import { type FC, useMemo } from "react";
import styled from "styled-components";
import { ButtonKind, ButtonSize, ButtonVariant, IconButton } from "@stubhub/broadway-web";
import { Col, Row } from "@viagogo/core/ui";
import { usePaginationNavigation } from "src/website-apps/shared/components/events-grid/pagination-controls/usePaginationNavigation";

const PageRow = styled(Row)`
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${(props) => props.theme.colors.textLinkPrimary};
  border: 1px solid ${(props) => props.theme.colors.borderDisabled};
  border-top: none;
  border-radius: ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(1)}
    ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(1)};
  padding: ${(props) => props.theme.spacing(2)} 0;
  width: 100%;
  margin: auto;
`;

const PaddedCol = styled(Col)`
  padding: ${({ theme }) => theme.spacing(0, 2)};
`;

const SelectedCol = styled(PaddedCol)`
  color: ${(props) => props.theme.colors.textDarker};
`;

interface Props {
  page: number;
  setPage: (setter: number | ((prev: number) => number)) => void;
  numPages: number;
}

export const PageNumbers: FC<Props> = ({ page, setPage, numPages }) => {
  const { BackIcon, ForwardIcon, ariaTextPrevious, ariaTextNext } = usePaginationNavigation();

  const pageElements = useMemo(
    () =>
      Array.from({ length: numPages }, (_: unknown, index) => {
        if (index === page) {
          return <SelectedCol key={index + 1}>{index + 1}</SelectedCol>;
        }
        if (index === page - 1 || index === page + 1 || index === 0 || index === numPages) {
          return (
            <PaddedCol key={index + 1}>
              <button
                type="button"
                onClick={() => {
                  setPage(index);
                }}
              >
                {index + 1}
              </button>
            </PaddedCol>
          );
        }
        return null;
      }),
    [numPages, page, setPage],
  );

  // Insert dots after creating all elements
  const elementsWithDots = useMemo(() => {
    const res = [];
    let prevDotsAdded = false;
    let nextDotsAdded = false;

    for (const [i, element] of pageElements.entries()) {
      if (element === null) {
        if (!prevDotsAdded && i < page) {
          prevDotsAdded = true;
          res.push(<PaddedCol key="prev-dots">...</PaddedCol>);
        } else if (!nextDotsAdded && i > page) {
          nextDotsAdded = true;
          res.push(<PaddedCol key="next-dots">...</PaddedCol>);
        }
      } else {
        res.push(element);
      }
    }
    return res;
  }, [page, pageElements]);

  return (
    <PageRow center={[true]}>
      {page > 0 && (
        <IconButton
          aria-label={ariaTextPrevious}
          kind={ButtonKind.Neutral}
          size={ButtonSize.Small}
          variant={ButtonVariant.Ghost}
          icon={BackIcon}
          onClick={() => {
            setPage((prev) => prev - 1);
          }}
        />
      )}
      {elementsWithDots}
      {page < numPages - 1 && (
        <IconButton
          aria-label={ariaTextNext}
          kind={ButtonKind.Neutral}
          size={ButtonSize.Small}
          variant={ButtonVariant.Ghost}
          icon={ForwardIcon}
          onClick={() => {
            setPage((prev) => prev + 1);
          }}
        />
      )}
    </PageRow>
  );
};
